import {Component, Input, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {ORGANISER_EVENT_EDIT, ORGANISER_EVENT_PREVIEW, ORGANISER_MEASUREMENTS_MANAGEMENT} from 'src/app/app-routing.module';
import {animate, state, transition, trigger, style} from '@angular/animations';
import {AbstractComponent} from "@components";
import {EventDetailsComponentType, EventFilter, EventItem, EventStatus, Lookups, Organiser} from "@model";
import {
  AuthenticationService,
  EventService,
  TOAST_STATE,
  ToastService
} from "@services";
import {alphabetical, max_length_255, required} from "@utility";
import {environment} from "@environment";

@Component({
  selector: 'app-event-basic-data',
  templateUrl: './event-basic-data.component.html',
  styleUrls: ['./event-basic-data.component.scss'],
  animations: [
    trigger('fadeInAnimation', [
      state('in', style({opacity: 1})),
      transition(':enter', [
        style({opacity: 0}),
        animate(1600)
      ])
    ])]
})
export class EventBasicDataComponent extends AbstractComponent implements OnInit {
  public static readonly EUR_CURRENCY: number = 7;
  currentDate: Date = new Date();

  @Input() currentEventItem: EventItem | undefined;
  @Input() eventDetailsComponentType: EventDetailsComponentType | undefined;
  @Input() currencies: Lookups[] = [];
  @Input() countries: Lookups[] = [];
  @Input() eventTypes: Lookups[] = [];
  @Input() languages: Lookups[] = [];
  @Input() statuses: Lookups[] = [];
  @Input() sports: Lookups[] = [];
  @Input() measurers: Organiser[] = [];
  @Input() organisers: Organiser[] = [];
  @Input() userRole: String | undefined;

  filter: EventFilter = new EventFilter();
  startDate: Date | undefined;
  endDate: Date | undefined;
  calendarStartDate: Date | undefined;

  inputForm = new FormGroup({
    organiserId: new FormControl(),
    measurerId: new FormControl(),
    name: new FormControl(),
    nameEng: new FormControl(),
    nameIta: new FormControl(),
    nameSlo: new FormControl(),
    nameSr: new FormControl(),
    nameBs: new FormControl(),
    status: new FormControl(),
    eventDates: new FormControl(),
    imageMedium: new FormControl(),
    imageIcon: new FormControl(),
    latitude: new FormControl(),
    longitude: new FormControl(),
    leagueName: new FormControl(),
    leagueNameEn: new FormControl(),
    leagueNameSl: new FormControl(),
    leagueNameSr: new FormControl(),
    leagueNameIt: new FormControl(),
    leagueNameBs: new FormControl(),
    defaultLanguage: new FormControl(),
    currency: new FormControl(),
    country: new FormControl(),
    city: new FormControl(),
    sportOne: new FormControl(),
    sportTwo: new FormControl(),
    extraLanguages: new FormControl(),
    eventType: new FormControl(),
    round: new FormControl(),
    locationExtraTag: new FormControl(),
    commonStartNumber: new FormControl(),
  });
  public isGeneralDataCollapsed: boolean = false;
  public isLocationDataCollapsed: boolean = false;

  constructor(private eventService: EventService,
              private toastService: ToastService, private router: Router,
              public translocoService: TranslocoService,
              public authenticationService: AuthenticationService) {
    super();
  }

  ngOnInit() {
    this.initForm();
    this.subscribeToDefaultLangChange();
    this.subscribeToLeagueNameChange();
    this.subscribeToOrganiserChange();
    this.eventItemChanged();
  }

  private initForm() {
    this.inputForm.controls.organiserId.addValidators([required]);
    this.inputForm.controls.defaultLanguage.addValidators([required]);
    this.inputForm.controls.name.addValidators([required, max_length_255]);
    this.inputForm.controls.nameEng.addValidators([max_length_255]);
    this.inputForm.controls.nameIta.addValidators([max_length_255]);
    this.inputForm.controls.nameSlo.addValidators([max_length_255]);
    this.inputForm.controls.nameSr.addValidators([max_length_255]);
    this.inputForm.controls.nameBs.addValidators([max_length_255]);

    this.inputForm.controls.eventDates.addValidators([required]);
    this.inputForm.controls.city.addValidators([required, alphabetical]);
    this.inputForm.controls.country.addValidators([required]);
    this.inputForm.controls.sportOne.addValidators([required]);
    this.inputForm.controls.status.addValidators([required]);
    this.inputForm.controls.eventType.addValidators([required]);

    this.inputForm.controls.leagueName.addValidators([max_length_255]);
    this.inputForm.controls.leagueNameIt.addValidators([max_length_255]);
    this.inputForm.controls.leagueNameEn.addValidators([max_length_255]);
    this.inputForm.controls.leagueNameSl.addValidators([max_length_255]);
    this.inputForm.controls.leagueNameSr.addValidators([max_length_255]);
    this.inputForm.controls.leagueNameBs.addValidators([max_length_255]);

    if (environment.eventImageRequired) {
      this.inputForm.controls.imageIcon.addValidators([required]);
      this.inputForm.controls.imageMedium.addValidators([required]);
    }
    if (this.eventDetailsComponentType === EventDetailsComponentType.COPY) {
      this.inputForm.controls.status.disable();
    }
    if (this.organisers.length === 1) {
      this.inputForm.controls.organiserId.setValue(this.organisers[0].id);
    }
  }

  private subscribeToDefaultLangChange() {
    this.subscriptions.push(this.inputForm.controls.defaultLanguage.valueChanges.subscribe((langId: number) => {
      if (langId) {
        if (Array.isArray(this.inputForm.controls.extraLanguages.value)) {
          let extraLanguages = this.inputForm.controls.extraLanguages.value ? [...this.inputForm.controls.extraLanguages.value] : [];
          let index: number = extraLanguages.indexOf(langId, 0);
          if (index > -1) {
            extraLanguages.splice(index, 1);
            this.inputForm.controls.extraLanguages.setValue(extraLanguages);
          }
        }
      }
    }));
  }


  private eventItemChanged(): void {
    if (this.currentEventItem) {
      this.inputForm.patchValue(this.currentEventItem);
      this.eventService.getFilteredEventStatuses(this.currentEventItem.id ?? undefined).subscribe((statuses: Lookups[]): void => {
        this.statuses = statuses
      })
      this.inputForm.controls.eventDates.setValue({from: this.currentEventItem.start, to: this.currentEventItem.end});

      if (!this.currentEventItem.currency) {
        this.inputForm.controls.currency.setValue(this.currentEventItem.organiser?.organiserPaymentData?.defaultCurrency ?? EventBasicDataComponent.EUR_CURRENCY);
      }
      if (!this.currentEventItem.status) {
        this.inputForm.controls.status.setValue(EventStatus.INACTIVE);
      }
      this.inputForm.updateValueAndValidity();
    } else {
      this.calendarStartDate = new Date();
      this.inputForm.controls.commonStartNumber.setValue(true);
    }
  }

  imageChange(image: string | undefined) {
    this.inputForm.controls.imageMedium.setValue(image);
  }

  logoChange(image: string | undefined) {
    this.inputForm.controls.imageIcon.setValue(image);
  }

  private subscribeToLeagueNameChange() {
    this.subscriptions.push(
      this.inputForm.controls.leagueName.valueChanges.subscribe(_ => {
        this.leagueNameChanged();
      }));
    this.subscriptions.push(
      this.inputForm.controls.leagueNameEn.valueChanges.subscribe(_ => {
        this.leagueNameChanged();
      }));
    this.subscriptions.push(
      this.inputForm.controls.leagueNameSl.valueChanges.subscribe(_ => {
        this.leagueNameChanged();
      }));
    this.subscriptions.push(
      this.inputForm.controls.leagueNameSr.valueChanges.subscribe(_ => {
        this.leagueNameChanged();
      }));
    this.subscriptions.push(
      this.inputForm.controls.leagueNameIt.valueChanges.subscribe(_ => {
        this.leagueNameChanged();
      }));
    this.subscriptions.push(
      this.inputForm.controls.leagueNameBs.valueChanges.subscribe(_ => {
        this.leagueNameChanged();
      }));
  }

  private leagueNameChanged() {
    if (this.isLeagueNameFilled()) {
      this.inputForm.controls.round.setValidators([required]);
    } else {
      this.inputForm.controls.round.removeValidators([required]);
    }
    this.inputForm.controls.round.updateValueAndValidity();
  }

  private isLeagueNameFilled() {
    return !!(this.inputForm.controls.leagueName.value ||
      this.inputForm.controls.leagueNameSr.value||
      this.inputForm.controls.leagueNameEn.value ||
      this.inputForm.controls.leagueNameIt.value ||
        this.inputForm.controls.leagueNameBs.value ||
      this.inputForm.controls.leagueNameSl.value);
  }

  private subscribeToOrganiserChange() {
    this.subscriptions.push(
      this.inputForm.controls.organiserId.valueChanges.subscribe((value: number): void => {
        let selectedOrganiser: Organiser = this.organisers.filter((organiser: Organiser): boolean => organiser.id === value)[0];
        if (selectedOrganiser?.organiserPaymentData) {
          this.inputForm.controls.currency.setValue(selectedOrganiser.organiserPaymentData?.defaultCurrency ?? EventBasicDataComponent.EUR_CURRENCY);
        } else {
          this.inputForm.controls.currency.setValue(EventBasicDataComponent.EUR_CURRENCY);
        }
      })
    )
  }

  goToEventPreview(): void {
    if (this.currentEventItem?.id) {
      this.eventService.setCurrentEvent(this.currentEventItem);
      this.router.navigate([ORGANISER_EVENT_PREVIEW], {queryParams: {eventId: this.currentEventItem.id}});
    }
  }

  goToEventMeasurements(): void {
      if (this.currentEventItem?.id) {
        this.eventService.setCurrentEvent(this.currentEventItem);
        this.router.navigate([ORGANISER_MEASUREMENTS_MANAGEMENT], {queryParams: {eventId: this.currentEventItem.id}});
      }
    }

  onSave(): void {
    this.markAllAsTouched(this.inputForm)
    if (!this.inputForm.valid) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("event.item.error.form"))
      return;
    } else {
      let eventDetails: EventItem = this.getEventFields();
      if (this.currentEventItem !== undefined) {
        if (this.eventDetailsComponentType === EventDetailsComponentType.COPY) {
          this.eventService.copyEvent(eventDetails, () => {
            this.toastService.showToast(TOAST_STATE.success,
              this.translocoService.translate("app.common.success.event.basic.data.edit"))
          }, () => {
            this.toastService.showToast(TOAST_STATE.danger,
              this.translocoService.translate("app.common.server.error"))
          }).subscribe((eventItem: EventItem | null) => {
            if (eventItem) {
              this.router.navigate([ORGANISER_EVENT_EDIT], {queryParams: {eventId: eventItem.id}});
            }
          });
        } else {
          this.eventService.updateEvent(eventDetails, () => {
            this.toastService.showToast(TOAST_STATE.success,
              this.translocoService.translate("app.common.success.event.basic.data.edit"))
          }, () => {
            this.toastService.showToast(TOAST_STATE.danger,
              this.translocoService.translate("app.common.server.error"))
          }).subscribe((eventItem: EventItem | null) => {
            if (eventItem) {
              if (eventDetails.id) {
                this.eventService.setCurrentEvent(eventDetails);
              } else {
                this.router.navigate([ORGANISER_EVENT_EDIT], {queryParams: {eventId: eventItem.id}});
              }
            }
          });
        }
      }
    }
  }

  getEventFields(): EventItem {
    this.checkValidators(this.inputForm);
    let eventDetails: EventItem = {...this.currentEventItem}
    eventDetails.organiserId = this.inputForm.value.organiserId;
    eventDetails.measurerId = this.inputForm.value.measurerId;
    eventDetails.organiser = this.organisers?.filter((organiser: Organiser) => organiser.id === this.inputForm.value.organiserId)[0];
    eventDetails.name = this.inputForm.value.name;
    eventDetails.nameEng = this.inputForm.value.nameEng;
    eventDetails.nameIta = this.inputForm.value.nameIta;
    eventDetails.nameSlo = this.inputForm.value.nameSlo;
    eventDetails.nameSr = this.inputForm.value.nameSr;
    eventDetails.nameBs = this.inputForm.value.nameBs;
    eventDetails.status = this.inputForm.value.status;
    eventDetails.start = this.inputForm.value.eventDates?.from;
    eventDetails.end = this.inputForm.value.eventDates?.to;
    eventDetails.latitude = this.inputForm.value.latitude;
    eventDetails.longitude = this.inputForm.value.longitude;
    eventDetails.leagueName = this.inputForm.value.leagueName;
    eventDetails.leagueNameEn = this.inputForm.value.leagueNameEn;
    eventDetails.leagueNameIt = this.inputForm.value.leagueNameIt;
    eventDetails.leagueNameSl = this.inputForm.value.leagueNameSl;
    eventDetails.leagueNameSr = this.inputForm.value.leagueNameSr;
    eventDetails.leagueNameBs = this.inputForm.value.leagueNameBs;
    eventDetails.defaultLanguage = this.inputForm.value.defaultLanguage;
    eventDetails.round = this.inputForm.value.round;
    eventDetails.currency = this.inputForm.value.currency;
    eventDetails.country = this.inputForm.value.country;
    eventDetails.city = this.inputForm.value.city;
    eventDetails.sportOne = this.inputForm.value.sportOne;
    eventDetails.sportTwo = this.inputForm.value.sportTwo;
    eventDetails.extraLanguages = this.inputForm.controls.extraLanguages.value;
    eventDetails.eventType = this.inputForm.value.eventType;
    eventDetails.locationExtraTag = this.inputForm.value.locationExtraTag;
    eventDetails.imageIcon = this.inputForm.value.imageIcon;
    eventDetails.imageMedium = this.inputForm.value.imageMedium;
    eventDetails.commonStartNumber = this.inputForm.value.commonStartNumber;
    return eventDetails;
  }

  getOptions(): Map<number, string> {
    const optionsMap: Map<number, string> = new Map<number, string>();
    this.languages.map((language: Lookups): void => {
      if (language.id && language.id !== this.inputForm.controls.defaultLanguage.value) {
        optionsMap.set(language.id, (language?.code) ? this.translocoService.translate('app.common.language.' + language.code) : "")
      }
    });
    return optionsMap;
  }

  onSelectLocation(loc: any): void {
    this.inputForm.controls.latitude.setValue(loc.lat);
    this.inputForm.controls.longitude.setValue(loc.lng);
  }

  onEraseLocation() {
    this.inputForm.controls.latitude.reset();
    this.inputForm.controls.longitude.reset();
  }
}


